import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { Typography } from 'src/typography';
import SvgPinIcon from 'src/assets/pin-icon';
import { CenteredColumn, StepPanel, StepFooter, StepHeader, StepButton } from '../shared-components';
import { GoodCannaIcon } from './good-canna-icon';

const DUTCHIE_TERMS_URL = 'https://dutchie.com/terms';
const DUTCHIE_PRIVACY_URL = 'https://dutchie.com/privacy';
const GOODCANNANOW_TERMS_URL = 'https://goodcannanow.com/terms-conditions-liability-waiver/';
const GOODCANNANOW_PRIVACY_URL = 'https://goodcannanow.com/privacy-policy/';

function getConsentCheckboxBorderColor(
  theme: {
    colors: {
      red: Record<number, string>;
      grey: Record<number, string>;
      blue: Record<number, string>;
    };
    customized?: { colors?: { buttonsLinks?: string } };
  },
  checked: boolean,
  hasError?: boolean
): string {
  if (hasError) return theme.colors.red[55];
  if (checked) return theme.customized?.colors?.buttonsLinks ?? theme.colors.blue[55];
  return theme.colors.grey[70];
}

type GetStartedStepProps = {
  onNext: () => void;
};

export const GetStartedStep = ({ onNext }: GetStartedStepProps): JSX.Element | null => {
  const { dispensary } = useDispensary();
  const [consentChecked, setConsentChecked] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);

  const handleGetStarted = (): void => {
    setShowConsentError(false);
    onNext();
  };

  const handleDisabledButtonClick = (): void => {
    setShowConsentError(true);
  };

  const handleConsentChange = (): void => {
    setConsentChecked((prev) => !prev);
    if (showConsentError) setShowConsentError(false);
  };

  return (
    <>
      <StepHeader showBackButton={false} />
      <StepPanel>
        <CenteredColumn>
          <GoodCannaIcon width={48} height={48} />
          <Typography.Heading tag='h2' size='medium'>
            Apply for your free medical certification with GoodCannaNow
          </Typography.Heading>
        </CenteredColumn>
      </StepPanel>

      <StepFooter>
        <Location>
          <SvgPinIcon height={20} width={20} />
          <Typography.Body size='large' weight='bold'>
            {dispensary?.name}
          </Typography.Body>
        </Location>
        <ConsentRow>
          {showConsentError && <ConsentError>You must agree to the terms to continue</ConsentError>}
          <ConsentCheckboxWrapper>
            <ConsentCheckboxInput
              type='checkbox'
              id='med-intake-consent'
              name='med-intake-consent'
              checked={consentChecked}
              onChange={handleConsentChange}
              aria-labelledby='med-intake-consent-label'
              aria-invalid={showConsentError}
            />
            <ConsentCheckboxLabel htmlFor='med-intake-consent'>
              <ConsentCheckboxBox $checked={consentChecked} $hasError={showConsentError} aria-hidden>
                {consentChecked && <ConsentCheckmark />}
              </ConsentCheckboxBox>
            </ConsentCheckboxLabel>
            <ConsentLabelText id='med-intake-consent-label'>
              I consent to the collection and transmission of my personal and medical information, including health
              conditions, medical history and government identification, by this platform to GoodCannaNow for the
              purpose of facilitating my medical evaluation and certification. If approved, my certification information
              will be transmitted to my selected dispensary. By checking this box and submitting the information on the
              following screens, I accept Dutchie's{' '}
              <ConsentLink href={DUTCHIE_TERMS_URL} target='_blank' rel='noopener noreferrer'>
                Terms of Service
              </ConsentLink>{' '}
              and{' '}
              <ConsentLink href={DUTCHIE_PRIVACY_URL} target='_blank' rel='noopener noreferrer'>
                Privacy Policy
              </ConsentLink>
              , and GoodCannaNow's{' '}
              <ConsentLink href={GOODCANNANOW_TERMS_URL} target='_blank' rel='noopener noreferrer'>
                Terms and Conditions, Liability Waiver and Medical Release Agreement
              </ConsentLink>{' '}
              and{' '}
              <ConsentLink href={GOODCANNANOW_PRIVACY_URL} target='_blank' rel='noopener noreferrer'>
                Privacy Policy
              </ConsentLink>
              .
            </ConsentLabelText>
          </ConsentCheckboxWrapper>
        </ConsentRow>
        <GetStartedButtonWrapper mt='16px'>
          {!consentChecked && (
            <DisabledButtonOverlay onClick={handleDisabledButtonClick} aria-hidden>
              {' '}
            </DisabledButtonOverlay>
          )}
          <StepButton disabled={!consentChecked} onClick={handleGetStarted}>
            Get Started
          </StepButton>
        </GetStartedButtonWrapper>

        <P size='medium' weight='bold'>
          <a href='https://app.goodcannanow.com/goodcannanow/'>Choose another dispensary</a>
        </P>
      </StepFooter>
    </>
  );
};

const P = styled(Typography.Body)`
  color: ${({ theme }) => theme.colors.grey[45]};
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spaces[2]};
`;

const GetStartedButtonWrapper = styled.div<{ mt?: string }>`
  position: relative;
  display: block;
  margin-top: ${({ mt, theme }) => mt ?? theme.spaces[4]};
`;

const DisabledButtonOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  cursor: pointer;
`;

const ConsentRow = styled.div`
  text-align: left;
  width: 100%;
  margin-top: ${({ theme }) => theme.spaces[4]};
  margin-bottom: ${({ theme }) => theme.spaces[2]};
`;

const ConsentCheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spaces[3]};
  text-align: left;
`;

const ConsentCheckboxLabel = styled.label`
  flex-shrink: 0;
  cursor: pointer;
`;

const ConsentCheckboxInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
  pointer-events: none;
`;

const ConsentCheckboxBox = styled.div<{ $checked: boolean; $hasError?: boolean }>`
  ${({ theme, $checked, $hasError }) => css`
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border: 2px solid ${getConsentCheckboxBorderColor(theme, $checked, $hasError)};
    border-radius: 3px;
    background-color: ${$checked
      ? theme.customized?.colors?.buttonsLinks ?? theme.colors.blue[55]
      : theme.colors.white};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
  `}
`;

const ConsentLabelText = styled.span`
  font-size: 11px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.primaryBack ?? theme.colors.grey[35]};
  text-align: left;
  cursor: default;
`;

const ConsentError = styled.span`
  display: block;
  margin-bottom: ${({ theme }) => theme.spaces[2]};
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.red[55]};
  text-align: left;
`;

const ConsentLink = styled.a`
  color: ${({ theme }) => theme.customized?.colors?.buttonsLinks ?? theme.colors.blue[55]};
  font-weight: 600;
  text-decoration: underline;

  &:hover {
    text-decoration: underline;
  }
`;

function ConsentCheckmark(): JSX.Element {
  return (
    <svg width='12' height='10' viewBox='0 0 12 10' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden>
      <path d='M1 5.5L4.5 9L11 1.5' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
}
